import { cancelOnSessionChange } from '../session-cancellation';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { mergeMap, map, catchError, tap, takeUntil, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationsService } from '@api/services/organizations.service';
import * as OrganizationsActions from '../../actions/organizations/support.actions';
import { MessageService } from 'primeng/api';
import { ApiDefOrganizationData } from '@api/models/api-def-organization-data';
import { ApiDefApiKeyData } from '@api/models/api-def-api-key-data';

@Injectable()
export class OrganizationsSupportEffects {
  loadSupportReports$;
    loadSupportReportById$;
    createSupportReport$;
    updateSupportReport$;
    deleteSupportReport$;
    createSupportReportSuccess$;
    createSupportReportFailure$;
    updateSupportReportSuccess$;
    updateSupportReportFailure$;
    deleteSupportReportSuccess$;
    deleteSupportReportFailure$;

  constructor(
    private logger: NGXLogger,
    private actions$: Actions,
    private organizationsService: OrganizationsService,
    private messageService: MessageService
  ) {
    this.loadSupportReports$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadSupportReports),
        mergeMap(({ organizationId, status, createdBy, priority, tags, page, limit, skipCount }) =>
          this.organizationsService.organizationsGetSupportReports({ 
            organizationId, 
            status, 
            createdBy, 
            priority, 
            tags, 
            page, 
            limit, 
            skipCount 
          }).pipe(
            map(response => {
              const reports = response.reports || [];
              return OrganizationsActions.loadSupportReportsSuccess({ 
                organizationId, 
                reports: reports.map(report => ({ 
                  data: report, 
                  loadedAt: new Date() 
                })),
                total: response.total
              });
            }),
            catchError(error => of(OrganizationsActions.loadSupportReportsFailure({ 
              organizationId, 
              error 
            }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.loadSupportReportById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadSupportReportById),
        mergeMap(({ organizationId, reportId }) =>
          this.organizationsService.organizationsGetSupportReportById({ 
            organizationId, 
            reportId 
          }).pipe(
            map(report => OrganizationsActions.loadSupportReportByIdSuccess({ 
              organizationId, 
              report: { 
                data: report, 
                loadedAt: new Date() 
              } 
            })),
            catchError(error => of(OrganizationsActions.loadSupportReportByIdFailure({ 
              organizationId, 
              reportId, 
              error 
            }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.createSupportReport$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.createSupportReport),
        mergeMap(({ organizationId, report }) =>
          this.organizationsService.organizationsCreateSupportReport({ 
            organizationId, 
            body: report 
          }).pipe(
            map(createdReport => OrganizationsActions.createSupportReportSuccess({ 
              organizationId, 
              report: { 
                data: createdReport, 
                loadedAt: new Date() 
              } 
            })),
            catchError(error => of(OrganizationsActions.createSupportReportFailure({ 
              organizationId, 
              error 
            }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.updateSupportReport$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.updateSupportReport),
        mergeMap(({ organizationId, reportId, changes }) =>
          this.organizationsService.organizationsUpdateSupportReport({ 
            organizationId, 
            reportId, 
            body: changes 
          }).pipe(
            map(updatedReport => OrganizationsActions.updateSupportReportSuccess({ 
              organizationId, 
              report: { 
                data: updatedReport, 
                loadedAt: new Date() 
              } 
            })),
            catchError(error => of(OrganizationsActions.updateSupportReportFailure({ 
              organizationId, 
              reportId, 
              error 
            }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.deleteSupportReport$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.deleteSupportReport),
        mergeMap(({ organizationId, reportId }) =>
          this.organizationsService.organizationsDeleteSupportReport({ 
            organizationId, 
            reportId 
          }).pipe(
            map(() => OrganizationsActions.deleteSupportReportSuccess({ 
              organizationId, 
              reportId 
            })),
            catchError(error => of(OrganizationsActions.deleteSupportReportFailure({ 
              organizationId, 
              reportId, 
              error 
            }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    // Show success/failure messages for support report operations
    this.createSupportReportSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.createSupportReportSuccess),
        tap(() => {
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Success', 
            detail: 'Support report created successfully.' 
          });
        })
      ),
      { dispatch: false }
    );

    this.createSupportReportFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.createSupportReportFailure),
        tap(({ error }) => {
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Failed to create support report.' 
          });
          this.logger.error('Create support report failed', error);
        })
      ),
      { dispatch: false }
    );

    this.updateSupportReportSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.updateSupportReportSuccess),
        tap(() => {
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Success', 
            detail: 'Support report updated successfully.' 
          });
        })
      ),
      { dispatch: false }
    );

    this.updateSupportReportFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.updateSupportReportFailure),
        tap(({ error }) => {
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Failed to update support report.' 
          });
          this.logger.error('Update support report failed', error);
        })
      ),
      { dispatch: false }
    );

    this.deleteSupportReportSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.deleteSupportReportSuccess),
        tap(() => {
          this.messageService.add({ 
            severity: 'success', 
            summary: 'Success', 
            detail: 'Support report deleted successfully.' 
          });
        })
      ),
      { dispatch: false }
    );

    this.deleteSupportReportFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.deleteSupportReportFailure),
        tap(({ error }) => {
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Failed to delete support report.' 
          });
          this.logger.error('Delete support report failed', error);
        })
      ),
      { dispatch: false }
    );
  }
}